import { stubStorage } from '@/utils/env'
import { useStateFromUiEvents, useDefaultObservable, uiEvents } from '@/utils/event'
import React from 'react'
import { ModalMenu } from './modal-menu'
import { MoreIcon } from './more-icon'
import { updateProduct } from '@/services/product'
import { useAppContext } from '@/pages/_app'
import { useVenueContext } from '@/services/venue'
import Link from 'next/link'
import { Product } from 'shared/types/api'
import { take } from 'rxjs/operators'
import { firstValueFrom } from 'rxjs'

declare global {
  interface AppUIEventMap {
    ProductModalMenuClick: {
      type: 'ProductModalMenuClick'
      product: Product
    }
    ProductModalMenuChooseItem: {
      type: 'ProductModalMenuChooseItem'
    }
    ProductModalMenuDeletePicture: {
      type: 'ProductModalMenuDeletePicture'
    }
  }
}

export type ProductModalMenuProps = {
  product: Product
  setProductOverriddenProperties: React.Dispatch<
    React.SetStateAction<{
      [key: string]: Partial<Product>
    }>
  >
}

export function ProductModalMenu(props: ProductModalMenuProps) {
  const venueContext = useVenueContext()!
  const { venueName, venue } = venueContext
  const { t, apiClient } = useAppContext()
  const showProductModalMenu$ = useStateFromUiEvents<boolean>({
    id: `showProductModalMenu/${props.product.productGroupName}/${props.product.name}`,
    startingValue: false,
    storage: stubStorage,
    process: (source, scan) => {
      return source.pipe(
        scan((state, event) => {
          switch (event.type) {
            case 'ProductModalMenuClick':
              const productUniqname = (product: Product) => {
                return JSON.stringify({
                  name: product.name,
                  productGroupName: product.productGroupName,
                })
              }
              return productUniqname(event.product) === productUniqname(props.product)
            case 'ProductModalMenuChooseItem':
              return false
            case 'ProductModalMenuDeletePicture':
              return false
            case 'ModalMenuBackClick':
              return event.modalMenuId === modalId ? false : state
          }
          return state
        }),
      )
    },
  })

  const showProductModalMenu = useDefaultObservable(showProductModalMenu$)
  const modalId = `productModalMenu/${props.product.productGroupName}/${props.product.name}`

  if (!showProductModalMenu) {
    return null
  }

  return (
    <ModalMenu id={modalId} title={props.product.title}>
      {venue.posType === 'local' && (
        <>
          <div
            onClick={() => {
              uiEvents.next({
                type: 'ProductModalMenuChooseItem',
              })
            }}
          >
            <Link
              href={`/venue/${venueName}/p/${props.product.productGroupName}/${props.product.name}/modifier-groups`}
              legacyBehavior
            >
              <button className="flex w-full justify-between p-5 focus:outline-none items-center">
                {t('common:modifiers')}
                <div>
                  <MoreIcon />
                </div>
              </button>
            </Link>
          </div>
          <div className="h-px bg-backOpaque dark:bg-backOpaqueDark"></div>
        </>
      )}

      {venue.posType === 'local' && (
        <>
          <button
            className="flex w-full justify-between p-5 focus:outline-none items-center"
            onClick={async () => {
              uiEvents.next({
                type: 'ProductModalMenuChooseItem',
              })

              const newIsInStoplist = !props.product.isInStopList

              props.setProductOverriddenProperties((currentValue) => {
                return {
                  ...currentValue,
                  [`${props.product.productGroupName}/${props.product.name}`]: {
                    isInStopList: newIsInStoplist,
                  },
                }
              })

              await updateProduct(apiClient, venueName, props.product, {
                isInStopList: newIsInStoplist,
              })
              venueContext.refreshVenue()
            }}
          >
            {!props.product.isInStopList
              ? t('venueName:addToSuspensionList')
              : t('venueName:removeFromSuspensionList')}

            <div>
              <svg
                width="21"
                height="23"
                viewBox="0 0 21 23"
                className="stroke-primary dark:stroke-primaryDark"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.0774 6.27686C18.3358 7.53565 19.1927 9.1393 19.5397 10.885C19.8867 12.6308 19.7083 14.4402 19.027 16.0846C18.3458 17.729 17.1922 19.1344 15.7122 20.1232C14.2323 21.112 12.4923 21.6398 10.7124 21.6398C8.9325 21.6398 7.19256 21.112 5.71258 20.1232C4.2326 19.1344 3.07905 17.729 2.39777 16.0846C1.7165 14.4402 1.5381 12.6308 1.88512 10.885C2.23215 9.1393 3.08903 7.53565 4.34741 6.27686"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.7173 1.63684V11.6368"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>

          <div className="h-px bg-backOpaque dark:bg-backOpaqueDark"></div>
        </>
      )}

      <button
        className="flex w-full justify-between p-5 focus:outline-none items-center"
        onClick={async () => {
          uiEvents.next({
            type: 'ProductModalMenuChooseItem',
          })

          const productIsInPopular = !!props.product.features.popular
          const newValue = productIsInPopular ? undefined : {}

          props.setProductOverriddenProperties((currentValue) => {
            return {
              ...currentValue,
              [`${props.product.productGroupName}/${props.product.name}`]: {
                features: {
                  ...props.product.features,
                  popular: newValue,
                },
              },
            }
          })

          await updateProduct(apiClient, venueName, props.product, {
            features: {
              ...props.product.features,
              popular: newValue,
            },
          })
          venueContext.refreshVenue()
        }}
      >
        {!props.product.features.popular
          ? t('venueName:addToPopular')
          : t('venueName:removeFromPopular')}

        <div className="relative left-[1px]">
          <svg
            fill="none"
            width="23"
            viewBox="0 0 23 22"
            className="stroke-primary dark:stroke-primaryDark"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.0249 1.67816L14.1149 7.93816L21.0249 8.94816L16.0249 13.8182L17.2049 20.6982L11.0249 17.4482L4.8449 20.6982L6.0249 13.8182L1.0249 8.94816L7.9349 7.93816L11.0249 1.67816Z"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>

      {props.product.pictures.length > 0 && (
        <>
          <div className="h-px bg-backOpaque dark:bg-backOpaqueDark"></div>

          <button
            className="flex w-full justify-between p-5 focus:outline-none items-center"
            onClick={async () => {
              uiEvents.next({
                type: 'ProductModalMenuDeletePicture',
              })
              if (!window.confirm(t('productName:doYouReallyWantToDeleteThePhoto'))) {
                return
              }

              props.setProductOverriddenProperties((currentValue) => {
                return {
                  ...currentValue,
                  [`${props.product.productGroupName}/${props.product.name}`]: {
                    pictures: [],
                  },
                }
              })

              await updateProduct(apiClient, venueName, props.product, {
                mainPhoto: null,
              })

              venueContext.refreshVenue()

              await firstValueFrom(venueContext.venueUpdated$.pipe(take(1)))

              props.setProductOverriddenProperties((currentValue) => {
                return {
                  ...currentValue,
                  [`${props.product.productGroupName}/${props.product.name}`]: {},
                }
              })
            }}
          >
            {t('venueName:deletePhoto')}

            <div className="relative left-[1px]">
              <svg
                width="23"
                height="23"
                viewBox="0 0 23 23"
                fill="none"
                className="stroke-primary dark:stroke-primaryDark"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.5 3.2677H3.19727C2.0927 3.2677 1.19727 4.16313 1.19727 5.2677V19.2677C1.19727 20.3723 2.0927 21.2677 3.19727 21.2677H17.1973C18.3018 21.2677 19.1973 20.3723 19.1973 19.2677V11"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.69727 10.2677C7.52569 10.2677 8.19727 9.59613 8.19727 8.7677C8.19727 7.93927 7.52569 7.2677 6.69727 7.2677C5.86884 7.2677 5.19727 7.93927 5.19727 8.7677C5.19727 9.59613 5.86884 10.2677 6.69727 10.2677Z"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M19.1973 15.2677L14 11.5L3.19727 21.2677"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.1973 1.2677L21.1973 7.2677"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21.1973 1.2677L15.1973 7.2677"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </>
      )}
    </ModalMenu>
  )
}
